import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Container,
  IconButton,
  SvgClose,
  Text,
  TextButton,
} from '@loomhq/lens';
import classnames from 'classnames';
import pluralize from 'pluralize';
import React, { useState } from 'react';

import {
  LARGE_DESKTOP_MIN_WIDTH,
  SMALL_DESKTOP_MAX_WIDTH,
} from 'constants/breakpoints';
import { WORKSPACE_SUGGESTION_CLICK } from 'constants/events';
import { ROLLOUT_WORKSPACE_CONSOLIDATE_EXISTING_USERS } from 'constants/featureFlag';
import { REQUEST_STATUSES } from 'constants/workspaceJoinRequests';
import * as analytics from 'utilities/analytics';
import { getCloudfrontURI } from 'utilities/avatar';
import RequestToJoinWorkspace from 'utilities/RequestToJoinWorkspace.graphql';

import GetSuggestedWorkspaceForBanner from './GetSuggestedWorkspaceForBanner';
import { REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED } from '../../../../../shared/constants/organizationInvitations';

import FacePile from 'components/face-pile';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import MediaQuery from 'components/media-query';
import WorkspaceLogo from 'components/workspace-logo';

import styles from './styles.module.less';

const JoinTeamBanner = ({ video }) => {
  const [dismissed, setDismissed] = useState(false);
  const [suggestedWorkspace, setSuggestedWorkspace] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const videoIsInSuggestedWorkspace =
    suggestedWorkspace &&
    Number(suggestedWorkspace.id) === video.organization_id;

  const { data } = useQuery(GetSuggestedWorkspaceForBanner, {
    onCompleted: data => {
      if (data?.result?.workspace) {
        if (data.result.isCurrentUserMember) {
          return setSuggestedWorkspace(null);
        }

        setSuggestedWorkspace(data.result.workspace);
        setAvatars(
          data.result.workspace.members.nodes.map(({ user }) => ({
            name: user.first_name,
            avatarSrc: getCloudfrontURI(user.avatars[0].thumb),
          }))
        );

        return;
      }

      setSuggestedWorkspace(null);
      setAvatars([]);
    },
    onError: () => {
      setSuggestedWorkspace(null);
      setAvatars([]);
    },
    fetchPolicy: 'cache-first',
  });

  const requestStatus = data?.result?.requestStatus;
  const hasPendingInvitation = data?.result?.hasPendingInvitation;
  const autoJoin = data?.result?.autoJoin;
  const requestPending = requestStatus === REQUEST_STATUSES.PENDING;

  const [requestToJoinWorkspace] = useMutation(RequestToJoinWorkspace);

  const memberCount =
    suggestedWorkspace?.counts?.users?.total_active_workspace_users;

  async function onRequestClick(workspaceId) {
    const { data } = await requestToJoinWorkspace({
      variables: { workspaceId },
    });

    analytics.track(WORKSPACE_SUGGESTION_CLICK, {
      source: 'share-page-banner',
      status: data.result.status,
      suggestedWorkspaceId: workspaceId,
    });

    if (data.result.status === REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED) {
      // The user's default workspace has changed, so let's take them to their new workspace
      window.location.href = '/';
    }
  }

  return (
    !dismissed &&
    !hasPendingInvitation &&
    requestStatus !== REQUEST_STATUSES.APPROVED &&
    videoIsInSuggestedWorkspace && (
      <div
        className={classnames(
          'flex justify:center width:full',
          styles.bannerContainer
        )}
      >
        <MediaQuery query={`(min-width: ${LARGE_DESKTOP_MIN_WIDTH}px)`}>
          <Container
            className={classnames(
              'flex items:center justify:spaceBetween width:full bgc:blurpleLight px:large py:medium',
              styles.joinTeamBanner
            )}
          >
            <div className="flex inline">
              <FacePile
                className={styles.shareBannerFacePile}
                userData={avatars}
                maxImages={4}
                alignment="left"
                avatarSize={4}
              />
              <Text isInline className="flex flexWrap items:center">
                &#128227;&nbsp;<strong>Your team is on Loom!</strong>
                &nbsp;Join&nbsp;
                <strong>{memberCount}</strong>
                &nbsp;
                {pluralize('other', memberCount)}
                &nbsp;in&nbsp;<strong>{suggestedWorkspace.name}</strong>
                &nbsp;and gain access to your team&apos;s content!
              </Text>
            </div>
            <div className="flex items:center">
              <TextButton
                className="c:blurpleDark mr:small"
                disabled={requestPending}
                onClick={() => {
                  onRequestClick(suggestedWorkspace.id);
                }}
              >
                {requestPending ? 'Requested' : autoJoin ? 'Join' : 'Request'}
              </TextButton>
              {!requestPending && (
                <TextButton onClick={() => setDismissed(true)}>
                  Dismiss
                </TextButton>
              )}
            </div>
          </Container>
        </MediaQuery>
        {/* End desktop layout */}
        {/* Begin mobile layout */}
        <MediaQuery query={`(max-width: ${SMALL_DESKTOP_MAX_WIDTH}px)`}>
          <Container
            className={classnames(
              'flex items:center justify:spaceBetween width:full bgc:blurpleLight pr:xsmall pl:large py:medium',
              styles.joinTeamBanner
            )}
          >
            <div className="flex items:center">
              <WorkspaceLogo workspace={suggestedWorkspace} size={5} />
              <div className="flex flexDirection:column ml:small">
                <div className="weight:medium">Join your team on Loom</div>
                <div className="c:bodyDimmed text:small">
                  {suggestedWorkspace.name} - {memberCount} members
                </div>
              </div>
            </div>
            <div className="flex items:center">
              <Button
                disabled={requestPending}
                onClick={() => {
                  onRequestClick(suggestedWorkspace.id);
                }}
                variant="primary"
              >
                {requestPending ? 'Pending' : 'Join'}
              </Button>
              <IconButton
                className="ml:small"
                altText="Dismiss"
                icon={<SvgClose />}
                onClick={() => setDismissed(true)}
              />
            </div>
          </Container>
        </MediaQuery>
      </div>
    )
  );
};

const WrappedJoinTeamBanner = props => {
  return (
    <FeatureFlaggedComponent
      flag={ROLLOUT_WORKSPACE_CONSOLIDATE_EXISTING_USERS}
      activationValues={[true]}
    >
      <JoinTeamBanner {...props} />
    </FeatureFlaggedComponent>
  );
};

export default WrappedJoinTeamBanner;
